import { useEffect, useMemo, useRef, memo, useState } from "react";
import { useLang } from "@hooks/useLang";
import { useTheme } from "styled-components";
import { TypeTimeInterval } from "@custom-types/Types";
import { useAnalyticsChartMenu } from "@hooks/useChartSelectMenu";
import { numberSymbol } from "@utils/Widget";
import ECharts from "@components/common/chart";
import { EChartsOption, ECharts as EchartType } from "echarts";
import {
  MAX_VISIBLE_LEGENDS,
  ANALYTICS_CHART_VIEW_TYPE,
  CHART_TYPE,
  ANALYTICS_CHART_TYPE
} from "@resources/Constants";
import _, { map } from "lodash";
import {
  AnalyticQueueDataResponse,
  AnalyticsDataResponse
} from "@queries/Analytics";
import useDetectOutsideClick from "@hooks/useDetectOutsideClick";
import { HVDropdown, HVIcon, HVScrollBar, HVTable } from "@repo/HVComponents";
import { chartRankPeopleCounts } from "@components/widgets/rank-peoplecounts/widget/Chart";
import { RankPeopleCountsDataChart } from "@components/widgets/rank-peoplecounts/widget";
import { SearchComparisonDateTime } from "@components/common/calendar/date-range-picker/Interfaces";
import trophy1 from "@resources/img/report/report-widget-top-count-1.png";
import trophy2 from "@resources/img/report/report-widget-top-count-2.png";
import trophy3 from "@resources/img/report/report-widget-top-count-3.png";
import * as S from "../ChartCard.styled";
import { BaseTimeProps, ChartCardProps } from "../Interfaces";
import { ICON } from "../../../../../constants/icons";
import {
  chartPeopeleObjectType,
  chartRetailObjectType,
  combinedPieAndLineChart
} from "../../PeopleChart";
import { getHighestTotal } from "../categoryChart/PreviewData";
import { CategoryTab } from "../categoryTab";
import { formatAnalyticsValue } from "../../chartUtils";

type ViewType = string;
type IHVTable = React.ComponentProps<typeof HVTable>;

export function TimeLine(props: ChartCardProps): JSX.Element {
  const {
    title,
    baseTime,
    options,
    analyticsData,
    viewType,
    setViewType,
    timeInterval,
    typeInterval,
    setType,
    type,
    analyticsFromReport,
    timeZone
  } = props;
  const { translate, language } = useLang();
  const theme = useTheme();
  const intervalMenuList = useAnalyticsChartMenu({
    baseTime
  });
  const [chartInstance, setChartInstance] = useState<EchartType>();
  const analyticsRef = useRef<HTMLDivElement | null>(null);
  const [showDropDownList, setShowDropDownList] = useState<boolean>(false);
  const [activeLocations, setActiveLocations] = useState<Set<string>>(
    new Set()
  );
  const checkLegendsChartAllowed = () => {
    return [
      ANALYTICS_CHART_TYPE.PEOPLE_QUEUES,
      ANALYTICS_CHART_TYPE.SHOPPING_CART_QUEUES,
      ANALYTICS_CHART_TYPE.VEHICLE_QUEUES,
      ANALYTICS_CHART_TYPE.PEOPLE_OCCUPANCY,
      ANALYTICS_CHART_TYPE.SHOPPING_CARTS_IN_USE,
      ANALYTICS_CHART_TYPE.VEHICLE_OCCUPANCY
    ].includes(type ?? "");
  };
  const visibleLegends =
    analyticsFromReport && checkLegendsChartAllowed()
      ? MAX_VISIBLE_LEGENDS - 2
      : MAX_VISIBLE_LEGENDS;

  const chartOptions: EChartsOption = useMemo(() => {
    switch (type) {
      case ANALYTICS_CHART_TYPE.RETAIL_VISITOR_AREA_TRAFFIC:
        return chartRetailObjectType(
          theme,
          timeInterval,
          activeLocations,
          baseTime,
          analyticsData as AnalyticQueueDataResponse[],
          translate
        );
      case ANALYTICS_CHART_TYPE.CROWD_COUNTING:
      case ANALYTICS_CHART_TYPE.SLIP_AND_FALL:
      case ANALYTICS_CHART_TYPE.SLOW_TRAFFIC:
        return combinedPieAndLineChart(
          theme,
          timeInterval,
          activeLocations,
          baseTime,
          props.viewType !== undefined ? props.viewType : "",
          analyticsData,
          timeZone,
          analyticsFromReport
        );
      default:
        return chartPeopeleObjectType(
          theme,
          timeInterval,
          activeLocations,
          baseTime,
          props.viewType !== undefined ? props.viewType : "",
          analyticsData,
          timeZone
        );
    }
  }, [
    type,
    theme,
    timeInterval,
    activeLocations,
    baseTime,
    props.viewType,
    analyticsData
  ]);

  const checkDisplayOptions = (renderType: string) => {
    if (!props.analyticsFromReport) return true;

    return props.displayOptions?.find((item) => item.includes(renderType));
  };

  const getBusiestData = (): RankPeopleCountsDataChart[] => {
    const allValues = analyticsData.map((item) => {
      return {
        name: item.locationName,
        value: item.total ?? 0,
        defaultColor: item.color,
        key: item.key
      };
    });
    return allValues.sort((a, b) => {
      if (a.value < b.value) {
        return 1;
      }

      if (a.value > b.value) {
        return -1;
      }

      return 0;
    });
  };

  const getOccupancyAnalyticsTableData = () => {
    const totalCount = analyticsData.reduce(
      (sum, item) => sum + (item?.total || 0),
      0
    );

    const daysDifference = Math.max(
      1,
      Math.ceil(
        (new Date(baseTime.end).getTime() -
          new Date(baseTime.start).getTime()) /
          (1000 * 60 * 60 * 24)
      )
    );

    const totalAverage = totalCount / daysDifference;

    return analyticsData.map((item, i) => {
      const total = item?.total || 0;
      const average = total / daysDifference;
      const percentage =
        average > 0 ? ((average / totalAverage) * 100).toFixed(1) : "";

      return {
        idx: i + 1,
        count: average,
        locationName: item.locationName,
        rate: percentage
      };
    });
  };

  const baseTimeToComparisonDateTime = (
    baseTime: BaseTimeProps
  ): SearchComparisonDateTime => {
    return {
      comparisonStart: baseTime.start,
      comparisonEnd: baseTime.end,
      ...baseTime
    };
  };

  const locationHighlightChart = (key: string | null) => () => {
    if (key && activeLocations.has(key)) {
      return;
    }
    const updatedData = analyticsData
      .filter((item) => !activeLocations.has(item.key))
      .map((seriesItem, index) => {
        const isHighlighted = key && seriesItem.key.includes(key);
        return {
          ...seriesItem,
          z: isHighlighted ? analyticsData.length : index,
          lineStyle: {
            opacity: isHighlighted || !key ? 1 : 0.2
          }
        };
      });
    chartInstance?.setOption({
      series: updatedData
    });

    if (
      props.type === ANALYTICS_CHART_TYPE.CROWD_COUNTING ||
      props.type === ANALYTICS_CHART_TYPE.SLIP_AND_FALL ||
      props.type === ANALYTICS_CHART_TYPE.SLOW_TRAFFIC
    ) {
      const selected = updatedData.findIndex((item) => item.key === key);
      if (selected !== -1) {
        chartInstance?.dispatchAction({
          type: "highlight",
          seriesId: "pie",
          dataIndex: selected
        });
      } else {
        analyticsData.forEach((item) => {
          chartInstance?.dispatchAction({
            type: "downplay",
            seriesId: "pie",
            name: item.locationName
          });
        });
      }
    }
  };

  const generateChartOption = () => {
    return chartRankPeopleCounts(
      theme,
      CHART_TYPE.BAR_HORIZONTAL,
      getBusiestData(),
      "Store",
      baseTimeToComparisonDateTime(baseTime),
      timeInterval,
      true,
      false,
      true
    );
  };

  const busiestData: RankPeopleCountsDataChart[] = useMemo(() => {
    if (props.type !== ANALYTICS_CHART_TYPE.RETAIL_VISITOR_AREA_TRAFFIC) {
      return [];
    }
    const allValues = analyticsData.map((item) => {
      return {
        name: item.locationName,
        value: parseFloat(
          (
            item.originalData?.reduce((sum, data) => {
              const key = viewType?.replace(
                /^(staying_|wait_|wait)/,
                ""
              ) as keyof typeof data;
              return sum + (Number(data[key]) || 0);
            }, 0) || 0
          ).toFixed(2)
        ),
        defaultColor: item.color
      };
    });

    return allValues.sort((a, b) => {
      if (a.value < b.value) {
        return 1;
      }

      if (a.value > b.value) {
        return -1;
      }

      return 0;
    });
  }, [analyticsData, viewType]);

  const rankChartOptions = useMemo(() => {
    return chartRankPeopleCounts(
      theme,
      CHART_TYPE.BAR_HORIZONTAL,
      busiestData,
      "Store",
      baseTimeToComparisonDateTime(baseTime),
      timeInterval,
      true,
      false,
      true,
      translate(viewType !== "count" ? "staying_time" : "count"),
      viewType === "count" ? 1 : 0,
      viewType !== "count"
    );
  }, [theme, timeInterval, busiestData, viewType]);

  useDetectOutsideClick(analyticsRef, () => setShowDropDownList(false));

  const updateSetTimeInterval = (value: TypeTimeInterval) => {
    setType?.(value);
    props.updatedAnalytics?.([], value, null, props.isSingleLocation);
  };

  const updatedLocationKey = (key: string) => {
    if (
      props.type === ANALYTICS_CHART_TYPE.CROWD_COUNTING ||
      props.type === ANALYTICS_CHART_TYPE.SLIP_AND_FALL ||
      props.type === ANALYTICS_CHART_TYPE.SLOW_TRAFFIC
    ) {
      chartInstance?.dispatchAction({
        type: "downplay",
        seriesId: "pie"
      });
    }
    setActiveLocations((prev) => {
      const updated = new Set(prev);
      if (updated.has(key)) {
        updated.delete(key);
      } else {
        updated.add(key);
      }
      return updated;
    });
  };

  const legendShowData = () => {
    return [...analyticsData.slice(0, visibleLegends)].map((data) => {
      return (
        <S.LegendItem
          key={data.key}
          onClick={() => updatedLocationKey(data.key)}
          disabled={activeLocations.has(data.key)}
          onMouseEnter={locationHighlightChart(data.key)}
          onMouseLeave={locationHighlightChart(null)}
          title={data.locationName}
          data-testid="legend-name"
        >
          <S.LegendCircle
            style={{
              backgroundColor: (data as AnalyticsDataResponse).color as string
            }}
          />
          {analyticsFromReport &&
          checkLegendsChartAllowed() &&
          data.locationName.length > 8
            ? `${data.locationName.slice(0, 10)}...`
            : data.locationName}
        </S.LegendItem>
      );
    });
  };
  const legendHideData = () => {
    return [...analyticsData.slice(visibleLegends, analyticsData.length)].map(
      (data) => {
        return (
          <S.LegendDropdownItem
            onClick={() => updatedLocationKey(data.key)}
            disabled={activeLocations.has(data.key)}
            onMouseEnter={locationHighlightChart(data.key)}
            onMouseLeave={locationHighlightChart(null)}
            key={data.key}
          >
            <S.LegendCircle
              style={{
                backgroundColor: (data as AnalyticsDataResponse).color as string
              }}
            />
            {data.locationName}
          </S.LegendDropdownItem>
        );
      }
    );
  };

  const determineTrophy = (
    sortedLocations: RankPeopleCountsDataChart[]
  ): RankPeopleCountsDataChart[] => {
    const locations = [...sortedLocations];

    const highestValue = locations[0].value;

    if (locations.every((company) => company.value === highestValue)) {
      return locations.map((company) => ({ ...company, trophy: trophy1 }));
    }

    if (locations[0].value === locations[1].value) {
      locations[0].trophy = trophy1;
      locations[1].trophy = trophy1;
      if (locations.length > 2) {
        locations[2].trophy = trophy3;
      }
    } else if (locations[1]?.value === locations[2]?.value) {
      locations[0].trophy = trophy1;
      locations[1].trophy = trophy2;
      if (locations.length > 2) {
        locations[2].trophy = trophy2;
      }
    } else {
      locations[0].trophy = trophy1;
      locations[1].trophy = trophy2;
      if (locations.length > 2) {
        locations[2].trophy = trophy3;
      }
    }

    return locations;
  };

  useEffect(() => {
    if (chartInstance) {
      chartInstance.setOption(chartOptions, true, true);
    }
  }, [chartInstance, chartOptions]);

  const getTotalBars = () => {
    return (analyticsData?.length && analyticsData.length) || 0;
  };

  const getChartHeight = () => {
    const totalBarLines = getTotalBars();
    if (totalBarLines <= 9) {
      return props.type === ANALYTICS_CHART_TYPE.RETAIL_VISITOR_AREA_TRAFFIC
        ? "279px"
        : "212px";
    }
    return `${totalBarLines * 30}px`;
  };

  const echartOptions = useMemo(() => {
    return generateChartOption();
  }, [theme, analyticsData, timeInterval]);

  const renderCumulativeGraphChart = () => {
    return (
      <>
        <S.InsightContent>
          <S.InsightHeading>
            {translate("analytics_chart_name.busiest_location")}
          </S.InsightHeading>

          <S.ChartRankPeopleContainer>
            {(() => {
              const firstThreeData = determineTrophy(
                getBusiestData().slice(0, 3)
              );
              return firstThreeData.map((item) => {
                const [inNumber, inSymbol] = numberSymbol(item.value as number);
                return (
                  <S.ChartRankPeopleWrap
                    borderShow={firstThreeData.length}
                    key={item.key}
                  >
                    <S.IconTrophy
                      src={
                        Array.isArray(item.trophy)
                          ? item.trophy.join(", ")
                          : item.trophy
                      }
                      alt="Trophy"
                    />
                    <S.ChartRankPeopleCount>
                      {inNumber}
                      {inSymbol}
                    </S.ChartRankPeopleCount>
                    <S.ChartRankPeopleLocation>
                      {item.name}
                    </S.ChartRankPeopleLocation>
                  </S.ChartRankPeopleWrap>
                );
              });
            })()}
          </S.ChartRankPeopleContainer>
        </S.InsightContent>
        <S.GraphChartContainer>
          <HVScrollBar
            scrollBarProps={{
              h: "100%",
              scrollbars: "y",
              type: "auto",
              p: 0
            }}
          >
            <ECharts
              option={echartOptions}
              style={{
                pointerEvents: "all",
                height: getChartHeight()
              }}
              settings={{ lazyUpdate: false }}
              mainChartType={`${props.type}_busiest_location`}
            />
          </HVScrollBar>
        </S.GraphChartContainer>
      </>
    );
  };

  const viewOptions = useMemo(
    () =>
      map(["count", "staying_time"], (type) => ({
        value: type,
        label: translate(type)
      })),
    [options?.viewList]
  );

  const renderRetailVisitorGraphChart = () => {
    return (
      <S.ChartWrap>
        <S.InsightContent>
          <S.InsightHeading>
            {translate("analytics_chart_name.retail_visitor_area_traffic")}{" "}
            {translate("ranking").toLocaleLowerCase()}
          </S.InsightHeading>
          {!analyticsFromReport && (
            <S.InsightCategorySwitch>
              <CategoryTab
                value={viewType}
                onChange={setViewType}
                options={viewOptions}
              />
            </S.InsightCategorySwitch>
          )}
        </S.InsightContent>
        <S.RetailGraphChartContainer>
          <HVScrollBar
            scrollBarProps={{
              h: "100%",
              scrollbars: "y",
              type: "auto",
              p: 0
            }}
          >
            <ECharts
              option={rankChartOptions}
              style={{
                pointerEvents: "all",
                height: getChartHeight()
              }}
              settings={{ lazyUpdate: false }}
              mainChartType={`${props.type}_ranking`}
            />
          </HVScrollBar>
        </S.RetailGraphChartContainer>
      </S.ChartWrap>
    );
  };

  const tableColumns = useMemo<IHVTable["columns"]>(
    () => [
      {
        header: translate("location"),
        accessorKey: "locationName",
        size: 40,
        mantineTableBodyCellProps: {
          style: {
            fontWeight: 500,
            fontSize: "14px",
            lineHeight: "18px",
            letterSpacing: "0%",
            color: "var(--hv-text-normal-primary)"
          }
        },
        mantineTableHeadCellProps: {
          style: {
            fontWeight: 500,
            fontSize: "12px",
            lineHeight: "18px",
            letterSpacing: "0%",
            color: "var(--hv-text-normal-secondary)",
            height: "32px",
            minHeight: "32px"
          }
        }
      },
      {
        header: translate("avg_count_rate"),
        size: 60,
        accessorKey: "count",
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: ({ row }) => {
          return (
            <S.Text>
              {formatAnalyticsValue(
                row.original.count as number,
                props.type || ""
              )}
              ({row.original.rate > 0 ? row.original.rate : 0}%)
            </S.Text>
          );
        },
        mantineTableHeadCellProps: {
          style: {
            fontWeight: 500,
            fontSize: "12px",
            lineHeight: "18px",
            justifyContent: "flex-end",
            textAlign: "right",
            height: "32px",
            minHeight: "32px",
            color: "var(--hv-text-normal-secondary)",
            minWidth: "auto",
            width: "auto"
          }
        },
        mantineTableBodyCellProps: {
          style: {
            fontWeight: 500,
            fontSize: "12px",
            lineHeight: "18px",
            height: "40px",
            minHeight: "40px",
            justifyContent: "flex-end",
            textAlign: "right",
            color: "var(--hv-text-normal-primary)"
          }
        }
      }
    ],
    [language]
  );

  const renderTable = () => {
    return (
      <HVTable
        selectedRowId=""
        tableOptions={{
          mantineTableBodyRowProps: {
            style: {
              fontWeight: 500,
              fontSize: "12px",
              lineHeight: "18px",
              height: "40px",
              minHeight: "40px",
              color: "var(--hv-text-normal-primary)"
            }
          },
          mantineTableHeadRowProps: {
            style: {
              height: "32px",
              minHeight: "32px",
              color: "var(--hv-text-normal-secondary)"
            }
          },
          mantinePaperProps: {
            style: { maxHeight: "320px", overflowY: "auto" }
          }
        }}
        columns={tableColumns}
        hideSelectionColumn
        data={getOccupancyAnalyticsTableData()}
      />
    );
  };

  const getTimeLineTitle = () => {
    switch (props.type) {
      case ANALYTICS_CHART_TYPE.CUMULATIVE_PEOPLE_COUNT:
        return "analytics_cumulative_people_visitors";
      case ANALYTICS_CHART_TYPE.PEOPLE_OCCUPANCY:
        return "analytics_people_occupancy_visitors";
      case ANALYTICS_CHART_TYPE.PEOPLE_QUEUES:
        return "analytics_people_queue";
      case ANALYTICS_CHART_TYPE.CUMULATIVE_SHOPPING_CART_COUNT:
        return "analytics_cumulative_shopping_cart_visitors";
      case ANALYTICS_CHART_TYPE.SHOPPING_CART_QUEUES:
        return "analytics_shopping_cart_queue_visitors";
      case ANALYTICS_CHART_TYPE.SHOPPING_CARTS_IN_USE:
        return "analytics_shopping_cart_in_use_visitors";
      case ANALYTICS_CHART_TYPE.CUMULATIVE_VEHICLE_COUNT:
        return "analytics_cumulative_vehicle_visitors";
      case ANALYTICS_CHART_TYPE.VEHICLE_OCCUPANCY:
        return "analytics_vehicle_occupancy_visitors";
      case ANALYTICS_CHART_TYPE.VEHICLE_QUEUES:
        return "analytics_vehicle_queue_visitors";
      case ANALYTICS_CHART_TYPE.CROWD_COUNTING:
        return "analytics_crowd_counting";
      case ANALYTICS_CHART_TYPE.SLIP_AND_FALL:
        return "analytics_slip_and_fall";
      case ANALYTICS_CHART_TYPE.RETAIL_VISITOR_AREA_TRAFFIC:
        return "analytics_retail_visitor_area_traffic";
      case ANALYTICS_CHART_TYPE.STOPPED_VEHICLE_ANALYTICS:
        return "analytics_stopped_vehicle";
      case ANALYTICS_CHART_TYPE.SLOW_TRAFFIC:
        return "analytics_slow_traffic";
      default:
        return "analytics_no_data";
    }
  };

  const columns = useMemo<IHVTable["columns"]>(
    () => [
      {
        header: translate("location"),
        accessorKey: "locationName",
        size: 80,
        mantineTableBodyCellProps: {
          style: {
            justifyContent: "flex-start",
            textAlign: "left",
            fontWeight: 500,
            fontSize: "14px",
            lineHeight: "18px",
            color: "var(--hv-text-normal-primary)"
          }
        },
        mantineTableHeadCellProps: {
          style: {
            justifyContent: "flex-start",
            textAlign: "left",
            fontWeight: 500,
            fontSize: "12px",
            lineHeight: "18px",
            color: "var(--hv-text-normal-secondary)"
          }
        }
      },
      {
        header: translate("avg_count"),
        accessorKey: "averageQueueCount",
        size: 30,
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: ({ row }) =>
          formatAnalyticsValue(
            row.original.averageQueueCount as number,
            props.type || ""
          ),
        mantineTableBodyCellProps: {
          style: {
            justifyContent: "center",
            textAlign: "center",
            fontWeight: 500,
            fontSize: "14px",
            lineHeight: "18px",
            color: "var(--hv-text-normal-primary)"
          }
        },
        mantineTableHeadCellProps: {
          style: {
            justifyContent: "center",
            textAlign: "center",
            fontWeight: 500,
            fontSize: "12px",
            lineHeight: "18px",
            color: "var(--hv-text-normal-secondary)"
          }
        }
      },
      {
        header: translate("wait_time"),
        accessorKey: "averageDwellTime",
        size: 30,
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: ({ row }) => {
          return `${row.original.averageDwellTime?.toFixed(0)}s`;
        },
        mantineTableBodyCellProps: {
          style: {
            justifyContent: "center",
            textAlign: "center",
            fontWeight: 500,
            fontSize: "14px",
            lineHeight: "18px",
            color: "var(--hv-text-normal-primary)"
          }
        },
        mantineTableHeadCellProps: {
          style: {
            justifyContent: "center",
            textAlign: "center",
            fontWeight: 500,
            fontSize: "12px",
            lineHeight: "18px",
            color: "var(--hv-text-normal-secondary)"
          }
        }
      }
    ],
    [language]
  );
  const renderQueueTable = () => {
    return (
      <HVTable
        columns={columns}
        hideSelectionColumn
        data={analyticsData}
        selectedRowId=""
        tableOptions={{
          enableSorting: true,
          mantineTableBodyRowProps: {
            style: {
              fontWeight: 500,
              fontSize: "12px",
              lineHeight: "18px",
              height: "40px",
              minHeight: "40px",
              color: "var(--hv-text-normal-primary)"
            }
          },
          mantineTableHeadRowProps: {
            style: {
              height: "32px",
              minHeight: "32px",
              color: "var(--hv-text-normal-secondary)"
            }
          },
          mantinePaperProps: {
            style: { maxHeight: "320px", overflowY: "auto" }
          }
        }}
      />
    );
  };

  const renderChartContent = () => {
    switch (props.type) {
      case ANALYTICS_CHART_TYPE.PEOPLE_QUEUES:
      case ANALYTICS_CHART_TYPE.SHOPPING_CART_QUEUES:
      case ANALYTICS_CHART_TYPE.VEHICLE_QUEUES:
        return <S.ChartGraphContent>{renderQueueTable()}</S.ChartGraphContent>;
      case ANALYTICS_CHART_TYPE.RETAIL_VISITOR_AREA_TRAFFIC:
      case ANALYTICS_CHART_TYPE.CUMULATIVE_PEOPLE_COUNT:
      case ANALYTICS_CHART_TYPE.CUMULATIVE_SHOPPING_CART_COUNT:
      case ANALYTICS_CHART_TYPE.CROWD_COUNTING:
      case ANALYTICS_CHART_TYPE.SLIP_AND_FALL:
      case ANALYTICS_CHART_TYPE.CUMULATIVE_VEHICLE_COUNT:
      case ANALYTICS_CHART_TYPE.STOPPED_VEHICLE_ANALYTICS:
      case ANALYTICS_CHART_TYPE.SLOW_TRAFFIC:
        return null;
      default:
        return <S.ChartGraphContent>{renderTable()}</S.ChartGraphContent>;
    }
  };

  const renderGraphChart = () => {
    switch (props.type) {
      case ANALYTICS_CHART_TYPE.CUMULATIVE_PEOPLE_COUNT:
      case ANALYTICS_CHART_TYPE.CUMULATIVE_SHOPPING_CART_COUNT:
      case ANALYTICS_CHART_TYPE.CUMULATIVE_VEHICLE_COUNT:
      case ANALYTICS_CHART_TYPE.STOPPED_VEHICLE_ANALYTICS:
        if (checkDisplayOptions("busiest_location")) {
          return (
            <>
              <S.ChartDivider separateTimeLines={props.separateTimeLines} />
              <S.ChartWrap>{renderCumulativeGraphChart()}</S.ChartWrap>
            </>
          );
        }
        return null;
      case ANALYTICS_CHART_TYPE.RETAIL_VISITOR_AREA_TRAFFIC:
        if (checkDisplayOptions("retail_visitor_area_traffic_chart")) {
          return (
            <>
              {props.separateTimeLines && (
                <S.ChartDivider separateTimeLines={props.separateTimeLines} />
              )}
              {renderRetailVisitorGraphChart()}
            </>
          );
        }
        return null;
      default:
        return null;
    }
  };

  const heading = useMemo(() => {
    switch (props.type) {
      case ANALYTICS_CHART_TYPE.RETAIL_VISITOR_AREA_TRAFFIC:
        return title;
      case ANALYTICS_CHART_TYPE.CROWD_COUNTING:
      case ANALYTICS_CHART_TYPE.SLIP_AND_FALL:
        return translate("analytics_accident_by_pie_chart_and_time_line", {
          title
        });
      case ANALYTICS_CHART_TYPE.SLOW_TRAFFIC:
        return translate("analytics_by_pie_chart_and_time_line", {
          title
        });
      default:
        return translate("analytics_time_line", { title });
    }
  }, [props.type, title]);

  const getLeftChartHeight = () => {
    switch (props.type) {
      case ANALYTICS_CHART_TYPE.CUMULATIVE_PEOPLE_COUNT:
      case ANALYTICS_CHART_TYPE.CUMULATIVE_SHOPPING_CART_COUNT:
      case ANALYTICS_CHART_TYPE.CUMULATIVE_VEHICLE_COUNT:
        return "224px";
      case ANALYTICS_CHART_TYPE.RETAIL_VISITOR_AREA_TRAFFIC:
        return "279px";
      default:
        return "248px";
    }
  };

  return (
    <S.ChartWrapContainer
      hasBorder={
        props.type !== ANALYTICS_CHART_TYPE.RETAIL_VISITOR_AREA_TRAFFIC &&
        !props.analyticsFromReport
      }
      separateTimeLines={props.separateTimeLines}
    >
      {checkDisplayOptions("timeline") && (
        <S.ChartWrap>
          <S.InsightContent>
            <S.InsightHeading>{heading}</S.InsightHeading>
            {(() => {
              const maxData = getHighestTotal(
                analyticsData,
                type || "",
                baseTime
              );

              const titleTimeLine = getTimeLineTitle();
              let translation = translate(titleTimeLine, {
                location: `<b>${maxData?.locationName || ""} ${
                  maxData?.cameraName ? `- ${maxData.cameraName}` : ""
                }</b>`,
                count: `<b>${formatAnalyticsValue(
                  maxData.total as number,
                  props.type || ""
                )}</b>`,
                time: `<b>${maxData.time}(s)</b>`
              });
              if (maxData.total === 0) {
                translation = translate("no_data_this_period");
              }
              return (
                <S.ChartContainer
                  dangerouslySetInnerHTML={{ __html: translation }}
                />
              );
            })()}
          </S.InsightContent>
          {checkDisplayOptions("timeline") && (
            <S.ChartGraphContainer>
              <S.ChartGraphContent>
                <S.ToggleAlignment
                  key={props.cardKey}
                  center={
                    props.type === ANALYTICS_CHART_TYPE.CROWD_COUNTING ||
                    props.type === ANALYTICS_CHART_TYPE.SLIP_AND_FALL ||
                    props.type === ANALYTICS_CHART_TYPE.SLOW_TRAFFIC
                  }
                >
                  <S.LegendContainer
                    textHeight={
                      analyticsFromReport && checkLegendsChartAllowed()
                    }
                  >
                    {legendShowData()}
                    <div ref={analyticsRef}>
                      {analyticsData.length > visibleLegends && (
                        <S.LegendMore>
                          <S.LegendButton
                            onClick={() =>
                              setShowDropDownList(!showDropDownList)
                            }
                            data-testid="legend-more-button"
                          >
                            <HVIcon name="add" svgProps={{ ...ICON.SIZE_12 }} />
                            {analyticsData.length - visibleLegends}{" "}
                            {translate("more")}
                          </S.LegendButton>
                          {showDropDownList && (
                            <S.LegendDropdown>
                              <HVScrollBar
                                scrollBarProps={{
                                  h: 232,
                                  scrollbars: "y",
                                  type: "auto",
                                  p: 0
                                }}
                              >
                                {legendHideData()}
                              </HVScrollBar>
                            </S.LegendDropdown>
                          )}
                        </S.LegendMore>
                      )}
                    </div>
                  </S.LegendContainer>
                  {props.type !==
                    ANALYTICS_CHART_TYPE.RETAIL_VISITOR_AREA_TRAFFIC &&
                    !analyticsFromReport && (
                      <S.ChartDropdown>
                        {(() => {
                          if (
                            !options?.viewList ||
                            options?.viewList.length === 0
                          ) {
                            return null;
                          }
                          return (
                            <HVDropdown
                              type="medium"
                              w={`calc(15px + ${
                                viewType?.toLowerCase().length
                              }ch)`}
                              miw={120}
                              maw={120}
                              mr={10}
                              options={
                                _.map(options?.viewList, (type) => ({
                                  value: type,
                                  label: translate(type)
                                })) as unknown as ViewType[]
                              }
                              data-testid="select-viewlist"
                              selectedItem={viewType || null}
                              styles={{
                                input: {
                                  borderRadius: 4,
                                  height: 24,
                                  borderColor: "var(--border-default)",
                                  background:
                                    "var(--hv-input-foreground-default)",
                                  minHeight: 24,
                                  fontSize: 12,
                                  fontWeight: 400,
                                  color: "var(--hv-text-normal-secondary)",
                                  paddingRight: 14
                                },
                                section: {
                                  width: 9,
                                  marginRight: 5
                                },
                                dropdown: {
                                  minWidth: 110
                                }
                              }}
                              getSelectedValue={(value) => {
                                value &&
                                  setViewType &&
                                  setViewType(value as ViewType);
                                if (
                                  value &&
                                  ANALYTICS_CHART_VIEW_TYPE.CUMULATIVE.includes(
                                    value as ViewType
                                  )
                                ) {
                                  props.updatedAnalytics?.(
                                    [],
                                    typeInterval,
                                    value as ViewType,
                                    props.isSingleLocation
                                  );
                                  props.setIsLoadingChart?.(false);
                                }
                              }}
                              selectWithArrow={false}
                            />
                          );
                        })()}
                        <HVDropdown
                          type="medium"
                          w={`calc(16px + ${
                            typeInterval.toLowerCase().length
                          }ch)`}
                          miw={120}
                          maw={100}
                          options={intervalMenuList}
                          selectedItem={typeInterval || null}
                          data-testid="select-typeinterval"
                          styles={{
                            input: {
                              borderRadius: 4,
                              height: 24,
                              borderColor: "var(--border-default)",
                              background: "var(--hv-input-foreground-default)",
                              minHeight: 24,
                              fontSize: 12,
                              fontWeight: 400,
                              color: "var(--hv-text-normal-secondary)",
                              paddingRight: 14
                            },
                            section: {
                              width: 9,
                              marginRight: 5
                            },
                            dropdown: {
                              minWidth: 110
                            }
                          }}
                          getSelectedValue={(value) => {
                            value &&
                              updateSetTimeInterval(value as TypeTimeInterval);
                            props.setIsLoadingChart?.(false);
                          }}
                          selectWithArrow={false}
                        />
                      </S.ChartDropdown>
                    )}
                </S.ToggleAlignment>
                <S.CursorZoom>
                  <ECharts
                    option={chartOptions}
                    onLoad={setChartInstance}
                    clearAfterUpdateChart
                    style={{
                      height: getLeftChartHeight()
                    }}
                    mainChartType={`${props.type}_time_line`}
                  />
                </S.CursorZoom>
              </S.ChartGraphContent>

              {renderChartContent()}
            </S.ChartGraphContainer>
          )}
        </S.ChartWrap>
      )}
      {renderGraphChart()}
    </S.ChartWrapContainer>
  );
}

export const TimeLinePresenter = memo(TimeLine);
